import React, { memo, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockIcon from "@mui/icons-material/Lock";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import {
  setUserAuth,
  setUserBusinessType,
  setUserLoanCategory,
  setUserPermission,
  setUserProfileData,
} from "../../../services/redux/slices/authSlice";
import { decrypt, encrypt } from "../../../utils/crypto";
import axiosInstance from "../../../services/api/interceptor/interceptor";
import {
  setIsBypassLogin,
  setIsEligibilityCheckSubmitted,
} from "../../../services/redux/slices/stepperDynamicFormSlice";
import { useDispatch, useSelector } from "react-redux";
import WalletIcon from "../../../assets/icons/wallet.png";
import FlightIcon from "../../../assets/icons/flight.png";
import HeadphoneIcon from "../../../assets/icons/headphone.png";
import Sidebar from "../components/Sidebar";
import StickyFooter from "../components/StickyFooter";
import { setIsStepsCompleted } from "../../../services/redux/slices/newJourneyEligibilitycheckSlice";

interface SuccessScreenProps {
  formData: Partial<any>;
  currentStep: number;
  progress: number;
  completedSteps: any;
}

const SuccessScreen: React.FC<SuccessScreenProps> = ({
  formData,
  currentStep,
  progress,
  completedSteps,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const dispatch = useDispatch();

  const isSuccessEligible = useSelector(
    (state: any) => state?.eligibilityCheck?.isSuccessEligible,
  );

  const breDetails = useSelector(
    (state: any) => state?.eligibilityCheck?.breDetails,
  );

  const [maxLoanAmt, setMaxLoanAmt] = useState<string>("");
  const [tenure, setTenure] = useState<string>("");

  useEffect(() => {
    dispatch(setIsStepsCompleted(true));
    if (breDetails?.Scheme_Available === "Yes") {
      const totalMonths = Number(breDetails?.Max_Tenure) ?? 0;
      const years = Math.floor(totalMonths / 12);
      const months = totalMonths % 12;

      const tenureDisplay =
        months > 0
          ? `${years} Year${years !== 1 ? "s" : ""} ${months} Month${months !== 1 ? "s" : ""}`
          : `${years} Year${years !== 1 ? "s" : ""}`;

      setTenure(tenureDisplay);

      setMaxLoanAmt(
        (() => {
          const raw = breDetails?.Max_Loan_Amount;
          if (raw === null || raw === undefined || raw === "") return "";
          const amount = typeof raw === "number" ? raw : Number(raw);
          if (isNaN(amount)) return "";
          const inLakh = amount / 100000;
          const formatted =
            inLakh % 1 === 0
              ? inLakh.toFixed(0)
              : inLakh.toFixed(1).replace(/\.?0+$/, "");
          return `₹${formatted} lakh`;
        })(),
      );
    }
  }, [breDetails?.Scheme_Available]);

  const universityName =
    typeof formData?.university === "object"
      ? formData?.university?.label || ""
      : formData?.university || formData?.universityLabel || "";

  const studentName = formData?.firstName
    ? `${typeof formData.firstName === "object" ? formData.firstName?.label : formData.firstName} ${typeof formData.lastName === "object" ? formData.lastName?.label : formData.lastName}`
    : "";

  const onContinue = () => {
    bypassLogin();
  };

  const bypassLogin = async () => {
    try {
      const encryptedKey = encrypt(formData?.phone_number);
      const resp: any = await axiosInstance.post(
        "api/v1/user-lead/guest-login",
        {
          key: encryptedKey,
          role: "applicant",
        },
      );
      if (resp.data?.status === "success") {
        const data: any = JSON.parse(decrypt(resp?.data?.data));
        dispatch(setUserProfileData(data?.user));
        dispatch(setUserPermission(data?.permission));
        dispatch(
          setUserBusinessType(
            data?.businessType.length > 0 ? data?.businessType?.[0] : {},
          ),
        );
        dispatch(setUserLoanCategory(data?.result));
        dispatch(setUserAuth(data?.token));
        dispatch(setIsBypassLogin(true));
        dispatch(setIsEligibilityCheckSubmitted(true));
      } else {
        console.error(resp.data?.message || "Bypass login failed");
      }
    } catch (error: any) {
      console.error(error?.response?.data?.message || "Bypass login failed");
    }
  };

  return (
    <Box>
      {/* ── Mobile Sidebar ── */}
      {isMobile && (
        <Box sx={{ borderTop: "1px solid #dfdada" }}>
          <Sidebar
            currentStep={currentStep === 4 ? 3 : currentStep}
            completedSteps={completedSteps}
          />
        </Box>
      )}
      {/* Hero Section */}
      <Box
        sx={{
          borderRadius: { xs: 0, sm: 3 },
          overflow: "hidden",
          position: "relative",
          minHeight: { xs: 200, md: 400 },
          display: "flex",
          alignItems: "flex-end",
          p: { xs: 3, md: 4 },
        }}
      >
        {/* Background pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          className={`unversity-banner-img ${!isSuccessEligible && "black-white-image"}`}
        />

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Chip
            icon={
              <WorkspacePremiumIcon
                sx={{ fontSize: 16, color: "#000 !important" }}
              />
            }
            label="ACHIEVEMENT UNLOCKED"
            size="small"
            sx={{
              bgcolor: "#F5A623",
              color: "#000",
              fontWeight: 700,
              fontSize: "0.7rem",
              mb: 2,
              letterSpacing: "0.05em",
              borderRadius: "20px",
              px: 0.5,
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2.4rem", md: "3.8rem" },
              fontWeight: 800,
              color: "white",
              mb: 1.5,
              lineHeight: 1.1,
            }}
          >
            {isSuccessEligible ? "Congratulations!" : "Sorry!"}
          </Typography>

          {isSuccessEligible ? (
            <Typography
              sx={{
                color: "#7EF6EE",
                fontSize: { xs: "0.95rem", md: "1.05rem" },
                fontWeight: 600,
                lineHeight: 1.5,
              }}
            >
              You can now study in your dream university <br />
              <Box component="span" sx={{ color: "#f5cf91", fontWeight: 700 }}>
                {universityName}
              </Box>
            </Typography>
          ) : (
            <Typography
              sx={{
                color: isSuccessEligible ? "rgba(255,255,255,0.9)" : "#7EF6EE",
                fontSize: "1rem",
                fontWeight: 500,
              }}
            >
              {`Hi ${studentName}, While we don't have an instant match, our team is on it. Our experts will reach out soon to help you.`}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ marginTop: "35px", padding: isMobile ? "0px 16px" : "" }}>
        {/* ── Main content grid ── */}
        <Grid container spacing={2.5} alignItems="stretch">
          {/* ── Left column: Loan Offer + Pre-Visa + Need Help ── */}
          <Grid
            item
            xs={12}
            md={isSuccessEligible ? 7 : 12}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            {/* {isSuccessEligible && (
            <Paper
              elevation={0}
              sx={{
                  p: { xs: 2.5, md: 4 },
                borderRadius: 3,
                bgcolor: "#fff",
                boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
              }}
            >
              <Typography
                sx={{
                    fontSize: "14pxm",
                    fontWeight: 600,
                  color: "text.secondary",
                  mb: 2.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                PERSONALIZED LOAN OFFER
              </Typography>

              <Box
                sx={{
                  display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  gap: { xs: 3, md: 5 },
                  flexWrap: "wrap",
                }}
              >
                <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
                    >
                    <Typography
                      sx={{
                          fontSize: { xs: "2rem", md: "56px" },
                        fontWeight: 800,
                          color: "#006763",
                        lineHeight: 1,
                      }}
                    >
                      {maxLoanAmt}
                    </Typography>
                  <Typography
                    sx={{
                          fontSize: "18px",
                          color: "#3D4948",
                    }}
                  >
                        Approved <br />
                        Amount
                  </Typography>
                    </Box>
                </Box>

                <Box>
                  <Typography
                    sx={{
                        fontSize: "14px",
                        color: "#006763",
                        fontWeight: 500,
                      letterSpacing: "0.05em",
                    }}
                  >
                    Repayment Tenure
                  </Typography>
                  <Typography
                    sx={{
                        fontSize: "22px",
                      fontWeight: 800,
                        color: "#191C1E",
                      lineHeight: 1,
                    }}
                  >
                    {tenure}
                  </Typography>
              </Box>

                  <img src={WalletIcon} />
              </Box>
            </Paper>
          )} */}

            {/* Pre-Visa + Need Help row */}
            <Grid container spacing={2} mt={0} sx={{ marginBottom: isMobile ? "300px" : 0 }}>
              <Grid
                item
                xs={12}
                sm={isSuccessEligible ? 7 : 6}
                // mb={!isSuccessEligible && isMobile ? 6 : 0}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: "24px",
                    bgcolor: "#fff",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    height: "100%",
                  }}
                >
                  <img src={FlightIcon} />
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "16px",
                        mb: 0.5,
                        color: "#006763",
                      }}
                    >
                      Pre-Visa Support
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "text.secondary",
                        lineHeight: 1.5,
                      }}
                    >
                      Get your loan sanction letter within 4 hours to expedite
                      your visa application.
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={isSuccessEligible ? 5 : 6}>
                <Paper
                  elevation={0}
                  sx={{
                    padding: { xs: "16px 24px", sm: "0px 24px" },
                    borderRadius: "24px",
                    bgcolor: "#fff",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    height: "100%",
                    cursor: "pointer",
                    transition: "box-shadow 0.2s",
                    "&:hover": {
                      boxShadow: "0 4px 24px rgba(27,107,90,0.14)",
                    },
                  }}
                >
                  <img src={HeadphoneIcon} />

                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "16px",
                        mb: 0.5,
                        color: "#191C1E",
                      }}
                    >
                      Need help?
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#3D4948",
                        fontWeight: 400,
                        textDecoration: "underline",
                      }}
                      onClick={() => {
                        window.open("https://www.avanse.com/contact-us", "_blank");
                      }}
                    >
                      Talk to a financial mentor
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* ── Right column: CTA Card ── */}
          {isSuccessEligible && !isMobile &&  (
            <Grid item xs={12} md={5} mb={isMobile ? "145px" : 0}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: "24px",
                  bgcolor: "#fff",
                  boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.6rem", md: "2rem" },
                    color: "primary.main",
                    mb: 1.5,
                    lineHeight: 1.2,
                  }}
                >
                  Ready to fly?{" "}
                  <Box component="span" sx={{ display: "block" }}>
                    Let's finalize.
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "text.secondary",
                    lineHeight: 1.6,
                    fontWeight: 500,
                    mb: 3,
                  }}
                >
                  To move to the final verification phase, we just need to
                  verify your identity using your PAN.
                </Typography>
                
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={onContinue}
                    sx={{
                      py: 1.75,
                      fontWeight: 700,
                      mb: 1.5,
                      fontSize: "1rem",
                      borderRadius: 2,
                    }}
                  >
                    Continue
                  </Button>
                
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.75,
                  }}
                >
                  <LockIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography
                    sx={{ fontSize: "0.72rem", color: "text.secondary" }}
                  >
                    256-bit Secure Encryption
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
      {isMobile && <StickyFooter progress={progress} onContinue={onContinue} />}
    </Box>
  );
};

export default memo(SuccessScreen);
